import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Asserts } from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import schema from './schema';
import { updateTemplate } from '../../../services/templates';
import { Template } from '../../../@types/domain';

type TemplateInputs = Asserts<typeof schema>;
type EditTemplateInputs = Required<TemplateInputs>;
type TemplateFormInputs = Omit<
  EditTemplateInputs,
  'id' | 'ref' | 'deleted' | 'createdBy'
>;

function useEditTemplateForm(template: Template) {
  if (!template.ref) {
    throw new Error('Template reference not found');
  }

  const queryClient = useQueryClient();

  const { mutateAsync: editTemplate, reset: resetMutation } = useMutation<
    void,
    Error,
    TemplateFormInputs
  >(['templates', 'updateTemplate'], (inputs) => {
    if (!template.ref) {
      throw new Error('Template reference not found');
    }
    return updateTemplate(template.ref, inputs);
  });

  const form = useForm<EditTemplateInputs>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: template.name,
      tenantId: template.tenantId,
      boardId: template.boardId,
      boardViewId: template.boardViewId,
      id: template.id,
      columns: [...template.columns],
    },
  });

  useEffect(() => {
    form.reset({
      name: template.name,
      tenantId: template.tenantId,
      boardId: template.boardId,
      boardViewId: template.boardViewId,
      id: template.id,
      columns: [...template.columns],
    });
    resetMutation();
  }, [template, form, resetMutation]);

  const onSubmit: SubmitHandler<EditTemplateInputs> = useCallback(
    async (data) => {
      const { id, ref, deleted, createdBy, ...inputs } = data;
      const result = await editTemplate(inputs);
      resetMutation();
      await queryClient.invalidateQueries([
        'templates',
        'getMyActiveTemplates',
      ]);
      return result;
    },
    [editTemplate, queryClient, resetMutation]
  );

  return { ...form, onSubmit: form.handleSubmit(onSubmit) };
}

export default useEditTemplateForm;
